import React from "react";
import { Typography } from "@material-ui/core";
import MunicipalPortalAAppService from "application/platform/municipal-portal-service";
import { CamaraFilter, CamaraListPage, DocumentButton } from "./camara-list.page";
import {
  MESES,
  RESULTADO_PRESTACAO_EXECUTIVO,
  RESULTADO_VOTACAO,
  STATUS_SESSAO,
  TIPO_ATIVIDADE,
  TIPO_ATO_NORMATIVO,
  TIPO_COMISSAO,
  TIPO_PROJETO,
  TIPO_SESSAO,
  VOTO,
  formatCurrency,
  formatDate,
  formatTime,
  labelOf,
  mapToOptions,
  yearOf,
  yearOptions,
} from "./camara-labels";

const includes = (text: string | null | undefined, term: string) => (text || "").toLowerCase().includes(term.trim().toLowerCase());

const distinctOptions = <T,>(rows: T[], getter: (row: T) => string | null | undefined) =>
  Array.from(new Set(rows.map(getter).filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));

const yearFilter = <T,>(getDate: (row: T) => string | null | undefined): CamaraFilter<T> => ({
  key: "ano",
  label: "Ano",
  type: "select",
  options: (rows) => yearOptions(rows.map((r) => yearOf(getDate(r)))),
  match: (row, value) => String(yearOf(getDate(row))) === value,
});

const truncate = (text: string | null | undefined, max = 140) => {
  const value = text || "";
  return value.length > max ? `${value.substring(0, max)}...` : value;
};

// ---------- Sessões Legislativas ----------
interface SessaoLegislativa {
  id: number;
  indTipoSessao: number;
  nomComissao?: string;
  dtaSessao: string;
  horaInicioSessao?: string;
  horaFimSessao?: string;
  indStatus: number;
  textoPauta?: string;
  pautaDocumentoUrl?: string;
  ataDocumentoUrl?: string;
  urlTransmissao?: string;
}

export const SessoesLegislativasPage = () => (
  <CamaraListPage<SessaoLegislativa>
    title="Sessões Legislativas"
    load={MunicipalPortalAAppService.listSessoesLegislativas}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma sessão legislativa encontrada."
    filters={[
      { key: "busca", label: "Pauta", type: "text", match: (r, v) => includes(r.textoPauta, v) },
      { key: "tipo", label: "Tipo", type: "select", options: mapToOptions(TIPO_SESSAO), match: (r, v) => String(r.indTipoSessao) === v },
      { key: "status", label: "Status", type: "select", options: mapToOptions(STATUS_SESSAO), match: (r, v) => String(r.indStatus) === v },
      yearFilter<SessaoLegislativa>((r) => r.dtaSessao),
    ]}
    columns={[
      { header: "Data", render: (r) => formatDate(r.dtaSessao) },
      {
        header: "Horário",
        render: (r) => (r.horaInicioSessao ? `${formatTime(r.horaInicioSessao)}${r.horaFimSessao ? ` às ${formatTime(r.horaFimSessao)}` : ""}` : "-"),
      },
      { header: "Tipo", render: (r) => labelOf(TIPO_SESSAO, r.indTipoSessao) },
      { header: "Comissão", render: (r) => r.nomComissao || "-" },
      { header: "Status", render: (r) => labelOf(STATUS_SESSAO, r.indStatus) },
      { header: "Pauta", render: (r) => truncate(r.textoPauta) || "-" },
      { header: "Pauta (doc.)", align: "center", render: (r) => <DocumentButton url={r.pautaDocumentoUrl} /> },
      { header: "Ata", align: "center", render: (r) => <DocumentButton url={r.ataDocumentoUrl} /> },
      { header: "Transmissão", align: "center", render: (r) => <DocumentButton url={r.urlTransmissao} label="Assistir" /> },
    ]}
  />
);

// ---------- Projetos de Lei ----------
interface DocumentoProjetoLei {
  id: number;
  descricao: string;
  dtaPublicacao: string;
  documentoUrl: string;
}

interface ProjetoLei {
  id: number;
  numProjeto: string;
  indTipoProjeto: number;
  textoEmenta: string;
  nomVereadorAutor?: string;
  nomVereadorRelator?: string;
  textoSituacaoAtual: string;
  dtaApresentacao: string;
  documentoUrl?: string;
  documentos?: DocumentoProjetoLei[];
}

export const ProjetosLeiPage = () => (
  <CamaraListPage<ProjetoLei>
    title="Projetos de Lei"
    load={MunicipalPortalAAppService.listProjetosLei}
    rowKey={(r) => r.id}
    emptyMessage="Nenhum projeto encontrado."
    filters={[
      { key: "busca", label: "Número ou ementa", type: "text", match: (r, v) => includes(r.numProjeto, v) || includes(r.textoEmenta, v) },
      { key: "tipo", label: "Tipo", type: "select", options: mapToOptions(TIPO_PROJETO), match: (r, v) => String(r.indTipoProjeto) === v },
      { key: "autor", label: "Autor", type: "select", options: (rows) => distinctOptions(rows, (r) => r.nomVereadorAutor), match: (r, v) => r.nomVereadorAutor === v },
      yearFilter<ProjetoLei>((r) => r.dtaApresentacao),
    ]}
    columns={[
      { header: "Número", render: (r) => r.numProjeto },
      { header: "Tipo", render: (r) => labelOf(TIPO_PROJETO, r.indTipoProjeto) },
      { header: "Ementa", render: (r) => r.textoEmenta },
      { header: "Autor", render: (r) => r.nomVereadorAutor || "-" },
      { header: "Situação", render: (r) => r.textoSituacaoAtual },
      { header: "Apresentação", render: (r) => formatDate(r.dtaApresentacao) },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
    renderDetail={(r) => (
      <>
        <Typography variant="body2">
          <strong>Relator:</strong> {r.nomVereadorRelator || "-"}
        </Typography>
        <Typography variant="subtitle2" style={{ marginTop: 12 }}>
          Documentos do projeto
        </Typography>
        {r.documentos && r.documentos.length > 0 ? (
          r.documentos.map((d) => (
            <div key={d.id} style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
              <span>
                {d.descricao} ({formatDate(d.dtaPublicacao)})
              </span>
              <DocumentButton url={d.documentoUrl} />
            </div>
          ))
        ) : (
          <Typography variant="body2">Nenhum documento adicional publicado.</Typography>
        )}
      </>
    )}
  />
);

// ---------- Atos Normativos ----------
interface AtoNormativo {
  id: number;
  indTipoAtoNormativo: number;
  numAto: string;
  textoEmenta: string;
  dtaPublicacao: string;
  documentoUrl?: string;
}

export const AtosNormativosPage = () => (
  <CamaraListPage<AtoNormativo>
    title="Atos Normativos"
    load={MunicipalPortalAAppService.listAtosNormativos}
    rowKey={(r) => r.id}
    emptyMessage="Nenhum ato normativo encontrado."
    filters={[
      { key: "busca", label: "Número ou ementa", type: "text", match: (r, v) => includes(r.numAto, v) || includes(r.textoEmenta, v) },
      { key: "tipo", label: "Tipo", type: "select", options: mapToOptions(TIPO_ATO_NORMATIVO), match: (r, v) => String(r.indTipoAtoNormativo) === v },
      yearFilter<AtoNormativo>((r) => r.dtaPublicacao),
    ]}
    columns={[
      { header: "Tipo", render: (r) => labelOf(TIPO_ATO_NORMATIVO, r.indTipoAtoNormativo) },
      { header: "Número", render: (r) => r.numAto },
      { header: "Ementa", render: (r) => r.textoEmenta },
      { header: "Publicação", render: (r) => formatDate(r.dtaPublicacao) },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
  />
);

// ---------- Votações Nominais ----------
interface VotoVereador {
  id: number;
  idVereador: number;
  nomVereador: string;
  indVoto: number;
}

interface VotacaoNominal {
  id: number;
  numProjeto?: string;
  dtaVotacao: string;
  textoAssunto: string;
  indVotacaoUnanime: boolean;
  indResultado?: number;
  documentoUrl?: string;
  votos?: VotoVereador[];
}

export const VotacoesNominaisPage = () => (
  <CamaraListPage<VotacaoNominal>
    title="Votações Nominais"
    load={MunicipalPortalAAppService.listVotacoesNominais}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma votação nominal encontrada."
    filters={[
      { key: "busca", label: "Assunto ou projeto", type: "text", match: (r, v) => includes(r.textoAssunto, v) || includes(r.numProjeto, v) },
      { key: "resultado", label: "Resultado", type: "select", options: mapToOptions(RESULTADO_VOTACAO), match: (r, v) => String(r.indResultado) === v },
      yearFilter<VotacaoNominal>((r) => r.dtaVotacao),
    ]}
    columns={[
      { header: "Data", render: (r) => formatDate(r.dtaVotacao) },
      { header: "Assunto", render: (r) => r.textoAssunto },
      { header: "Projeto", render: (r) => r.numProjeto || "-" },
      { header: "Resultado", render: (r) => labelOf(RESULTADO_VOTACAO, r.indResultado) },
      { header: "Unânime", render: (r) => (r.indVotacaoUnanime ? "Sim" : "Não") },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
    renderDetail={(r) => {
      const votos = r.votos || [];
      if (votos.length === 0) return <Typography variant="body2">Os votos desta votação ainda não foram registrados.</Typography>;

      const totals = Object.keys(VOTO)
        .map((id) => `${VOTO[Number(id)]}: ${votos.filter((v) => v.indVoto === Number(id)).length}`)
        .join("  |  ");

      return (
        <>
          <Typography variant="subtitle2">{totals}</Typography>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 8, marginTop: 12 }}>
            {votos.map((v) => (
              <div key={v.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, borderBottom: "1px solid #eee", padding: "4px 0" }}>
                <span>{v.nomVereador}</span>
                <strong>{labelOf(VOTO, v.indVoto)}</strong>
              </div>
            ))}
          </div>
        </>
      );
    }}
  />
);

// ---------- Cota Parlamentar ----------
interface CotaParlamentar {
  id: number;
  nomVereador: string;
  numAno: number;
  numMes: number;
  valorCota: number;
  valorUtilizado?: number | null;
  textoDescricaoDespesa?: string;
  documentoUrl?: string;
}

export const CotasParlamentaresPage = () => (
  <CamaraListPage<CotaParlamentar>
    title="Cota Parlamentar"
    load={MunicipalPortalAAppService.listCotasParlamentares}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma cota parlamentar encontrada."
    filters={[
      { key: "vereador", label: "Vereador", type: "select", options: (rows) => distinctOptions(rows, (r) => r.nomVereador), match: (r, v) => r.nomVereador === v },
      { key: "ano", label: "Ano", type: "select", options: (rows) => yearOptions(rows.map((r) => r.numAno)), match: (r, v) => String(r.numAno) === v },
      { key: "mes", label: "Mês", type: "select", options: mapToOptions(MESES), match: (r, v) => String(r.numMes) === v },
    ]}
    columns={[
      { header: "Vereador", render: (r) => r.nomVereador },
      { header: "Competência", render: (r) => `${labelOf(MESES, r.numMes)}/${r.numAno}` },
      { header: "Valor da cota", align: "right", render: (r) => formatCurrency(r.valorCota) },
      { header: "Utilizado", align: "right", render: (r) => formatCurrency(r.valorUtilizado) },
      {
        header: "Saldo",
        align: "right",
        render: (r) => (r.valorUtilizado === undefined || r.valorUtilizado === null ? "-" : formatCurrency(r.valorCota - r.valorUtilizado)),
      },
      { header: "Descrição da despesa", render: (r) => r.textoDescricaoDespesa || "-" },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
  />
);

// ---------- Atividade Parlamentar ----------
interface AtividadeParlamentar {
  id: number;
  nomVereador: string;
  indTipoAtividade: number;
  nomTitulo?: string;
  textoDescricao: string;
  dtaAtividade: string;
  documentoUrl?: string;
}

export const AtividadesParlamentaresPage = () => (
  <CamaraListPage<AtividadeParlamentar>
    title="Atividade Parlamentar"
    load={MunicipalPortalAAppService.listAtividadesParlamentares}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma atividade parlamentar encontrada."
    filters={[
      { key: "busca", label: "Título ou descrição", type: "text", match: (r, v) => includes(r.nomTitulo, v) || includes(r.textoDescricao, v) },
      { key: "vereador", label: "Vereador", type: "select", options: (rows) => distinctOptions(rows, (r) => r.nomVereador), match: (r, v) => r.nomVereador === v },
      { key: "tipo", label: "Tipo", type: "select", options: mapToOptions(TIPO_ATIVIDADE), match: (r, v) => String(r.indTipoAtividade) === v },
      yearFilter<AtividadeParlamentar>((r) => r.dtaAtividade),
    ]}
    columns={[
      { header: "Data", render: (r) => formatDate(r.dtaAtividade) },
      { header: "Vereador", render: (r) => r.nomVereador },
      { header: "Tipo", render: (r) => labelOf(TIPO_ATIVIDADE, r.indTipoAtividade) },
      { header: "Título", render: (r) => r.nomTitulo || "-" },
      { header: "Descrição", render: (r) => r.textoDescricao },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
  />
);

// ---------- Comissões ----------
interface Comissao {
  id: number;
  nomComissao: string;
  indTipo: number;
  textoDescricao?: string;
  nomVereadorPresidente?: string;
  nomVereadorRelator?: string;
  nomVereadorMembro?: string;
}

export const ComissoesPage = () => (
  <CamaraListPage<Comissao>
    title="Comissões"
    load={MunicipalPortalAAppService.listComissoes}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma comissão encontrada."
    filters={[
      { key: "busca", label: "Nome da comissão", type: "text", match: (r, v) => includes(r.nomComissao, v) },
      { key: "tipo", label: "Tipo", type: "select", options: mapToOptions(TIPO_COMISSAO), match: (r, v) => String(r.indTipo) === v },
    ]}
    columns={[
      { header: "Comissão", render: (r) => r.nomComissao },
      { header: "Tipo", render: (r) => labelOf(TIPO_COMISSAO, r.indTipo) },
      { header: "Presidente", render: (r) => r.nomVereadorPresidente || "-" },
      { header: "Relator", render: (r) => r.nomVereadorRelator || "-" },
      { header: "Membro", render: (r) => r.nomVereadorMembro || "-" },
      { header: "Descrição", render: (r) => r.textoDescricao || "-" },
    ]}
  />
);

// ---------- Prestação de Contas do Executivo ----------
interface PrestacaoContaExecutivo {
  id: number;
  numExercicio: number;
  nomGestor: string;
  numProcesso?: string;
  dtaJulgamento?: string;
  indResultado: number;
  documentoUrl?: string;
  valorReceitaArrecadada?: number | null;
  valorDespesaArrecadada?: number | null;
}

export const PrestacoesContaExecutivoPage = () => (
  <CamaraListPage<PrestacaoContaExecutivo>
    title="Prestação de Contas do Executivo"
    load={MunicipalPortalAAppService.listPrestacoesContaExecutivo}
    rowKey={(r) => r.id}
    emptyMessage="Nenhuma prestação de contas encontrada."
    filters={[
      { key: "ano", label: "Exercício", type: "select", options: (rows) => yearOptions(rows.map((r) => r.numExercicio)), match: (r, v) => String(r.numExercicio) === v },
      { key: "resultado", label: "Resultado", type: "select", options: mapToOptions(RESULTADO_PRESTACAO_EXECUTIVO), match: (r, v) => String(r.indResultado) === v },
    ]}
    columns={[
      { header: "Exercício", render: (r) => r.numExercicio },
      { header: "Gestor", render: (r) => r.nomGestor },
      { header: "Processo", render: (r) => r.numProcesso || "-" },
      { header: "Julgamento", render: (r) => formatDate(r.dtaJulgamento) },
      { header: "Resultado", render: (r) => labelOf(RESULTADO_PRESTACAO_EXECUTIVO, r.indResultado) },
      { header: "Receita arrecadada", align: "right", render: (r) => formatCurrency(r.valorReceitaArrecadada) },
      { header: "Despesa", align: "right", render: (r) => formatCurrency(r.valorDespesaArrecadada) },
      { header: "Documento", align: "center", render: (r) => <DocumentButton url={r.documentoUrl} /> },
    ]}
  />
);
